import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MatchLevel, MATCH_LEVEL_LABEL } from '../../utils/diagramTag';
import './MatchStrength.css';

// Match strength — four ascending bars + one word (Match Strength Bars spec v3).
// Bar count encodes the level; the word always says it, so meaning never rests
// on colour or length alone. `level === null` = "No estimate": an empty track,
// never one bar — "we never ranked this" is not "we ranked it very low".

type Tr = (key: string, def: string, vars?: Record<string, unknown>) => string;

export interface MatchStrengthProps {
  level: MatchLevel | null;
  /** md = part rows (3px bars, 11px word) · sm = headers, legend, sort (2px, 10px). */
  size?: 'md' | 'sm';
  /** Translator in the viewer's (key, default, vars) shape; English fallback. */
  tr?: Tr;
  /** Sits on a selected / lilac-tinted row: darker unfilled track. */
  onTint?: boolean;
  /** Hover/focus tooltip. Off for the legend and sort control (decorative). */
  tooltip?: boolean;
  /** Extra evidence lines shown under the standard tooltip copy. */
  evidence?: { k: string; text: string; gap?: boolean }[];
  /** Hide the word (legend glyph-only use). The aria-label still names it. */
  hideLabel?: boolean;
  className?: string;
}

const fallbackTr: Tr = (_k, def, vars) =>
  Object.entries(vars || {}).reduce((acc, [k, v]) => acc.split(`{{${k}}}`).join(String(v)), def);

export const matchLevelWord = (level: MatchLevel | null, tr: Tr = fallbackTr) => {
  const l = MATCH_LEVEL_LABEL[level ?? 'none'];
  return tr(l.key, l.defaultValue);
};

const MatchStrength: React.FC<MatchStrengthProps> = ({
  level, size = 'md', tr = fallbackTr, onTint, tooltip = true, evidence, hideLabel, className,
}) => {
  const tipId = useId();
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const open = tooltip && (pinned || hovered || focused);
  // The tooltip floats in a body portal: rows live in cards with
  // overflow:hidden inside a scrolling list, which would clip it.
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useEffect(() => {
    if (!open) { setPos(null); return; }
    const place = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const W = 240, H = 120, M = 8;
      const vw = window.innerWidth, vh = window.innerHeight;
      const left = Math.max(M, Math.min(vw - W - M, r.right - W));
      const top = r.bottom + 6 + H > vh ? Math.max(M, r.top - 6 - H) : r.bottom + 6;
      setPos({ left, top });
    };
    place();
    const close = () => { setPinned(false); setHovered(false); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', place); };
  }, [open]);
  const word = matchLevelWord(level, tr);
  const aria = level == null
    ? tr('matchLevel.ariaNone', 'No match estimate')
    : tr('matchLevel.aria', '{{level}} match, {{n}} of 4', { level: word, n: level });
  const cls = [
    'match-strength', `match-strength--${size}`,
    onTint ? 'on-tint' : '', className || '',
  ].filter(Boolean).join(' ');
  return (
    <span
      ref={anchorRef}
      className={cls}
      data-level={level ?? 'none'}
      data-testid="match-strength"
      role="img"
      aria-label={aria}
      aria-describedby={tooltip ? tipId : undefined}
      tabIndex={tooltip ? 0 : undefined}
      onClick={tooltip ? (e) => { e.stopPropagation(); setPinned(p => !p); } : undefined}
      onMouseEnter={tooltip ? () => setHovered(true) : undefined}
      onMouseLeave={tooltip ? () => setHovered(false) : undefined}
      onFocus={tooltip ? () => setFocused(true) : undefined}
      onBlur={tooltip ? () => { setFocused(false); setPinned(false); } : undefined}
      onKeyDown={tooltip ? (e) => { if (e.key === 'Escape' && open) { e.stopPropagation(); setPinned(false); setHovered(false); setFocused(false); } } : undefined}
    >
      <span className="match-strength__bars" aria-hidden="true">
        {[1, 2, 3, 4].map(i => (
          <span key={i} className={`match-strength__bar${level != null && i <= level ? ' is-on' : ''}`} />
        ))}
      </span>
      {!hideLabel && <span className="match-strength__label" aria-hidden="true">{word}</span>}
      {tooltip && (() => {
        const tip = (
        <span id={tipId} role="tooltip" className={`match-strength__tip${open && pos ? ' is-open' : ''}`}
          style={open && pos ? { left: pos.left, top: pos.top } : undefined}
          onClick={(e) => e.stopPropagation()}>
          {level == null ? (
            <span className="match-strength__tip-title">{tr('matchLevel.tooltipNone', 'No estimate — this part was not ranked against your search.')}</span>
          ) : (
            <>
              <span className="match-strength__tip-title">{tr('matchLevel.tooltipTitle', '{{level}} match · {{n}} of 4', { level: word, n: level })}</span>
              {tr('matchLevel.tooltipBody', 'How confident the agent is that this part answers your search. Not a guarantee of fitment or stock — check the diagram.')}
            </>
          )}
          {evidence && evidence.length > 0 && (
            <span className="match-strength__tip-evidence">
              {evidence.map(i => <span key={i.k} className={i.gap ? 'is-gap' : undefined}>{i.text}</span>)}
            </span>
          )}
        </span>
        );
        // Closed: kept inline (hidden) so aria-describedby always resolves.
        return open && pos && typeof document !== 'undefined' ? createPortal(tip, document.body) : tip;
      })()}
    </span>
  );
};

export default MatchStrength;
